import { AxiosError } from 'axios';
import { omit, uniqBy } from 'lodash-es';
import { differenceInMilliseconds } from 'date-fns';
import {
  AccommodationLeg,
  AviaLegTypes,
  BaseLegLocationType,
  CustomLegLocationType,
  CustomLocationType,
  GroundLegTypes,
  HubsDataRequestList,
  HubsRequestParams,
  HubTypeEnum,
  LegIdentifiers,
  LegLocationType,
  LegLocationTypeEnum,
  RemoveLogisticsRoutesParams,
  ResponseRouteLegTypeEnum,
  RouteLegResponseType,
  RouteLegType,
  RouteLegTypeEnum,
  RouteLocationType,
} from '@/api/apis/route';
import { AirportFboType, AirportType, Hospital, Hub, TXCenter } from '@/api/apis/facilities';
import { airportsQuery } from '@/queries/airports';
import { queryClient } from '@/queries';
import { homeLocationQueries } from '@/queries/homelocation';
import { routePlannerQueries } from '@/queries/route-planner/route-planner.query';
import { routePlannerQueryKeys } from '@/queries/route-planner/route-planner.keys';
import { hubQueries } from '@/queries/hub';
import { EmployeeLocation } from '@/api/types';
import { api } from '@/api';
import { hereMapsQueries } from '@/queries/here-maps';
import { HereGeocodeItemType } from '@/api/apis/here-maps';
import { txCenterQueries } from '@/queries/tx-centers';
import { hospitalQueries } from '@/queries/hospital';

export const LOGISTICS_VERSION_MISMATCH_ERROR_CODE = 'LogisticsVersionMismatch';
export const BASE_FBO_NAME = 'signature aviation';

export const getDefaultFbo = (fboList: AirportFboType[] = [], currentFboId?: string): AirportFboType | null => {
  if (!fboList.length) return null;

  if (currentFboId) {
    const isFboInCurrentList = fboList.find((f) => f.id === currentFboId);

    if (isFboInCurrentList) return null;
  }

  const signatureFbo = fboList.find((f) => f.name.toLowerCase().includes(BASE_FBO_NAME));

  return signatureFbo || fboList[0];
};

// Case DH/TXC are known from the case details, so they are not fetched
export type CaseLocationIds = {
  donorHospitalId?: string;
  txCenterId?: string;
};

const isCaseLocation = (location: Pick<LegLocationType, 'id' | 'type'>, caseLocationIds: CaseLocationIds) => {
  switch (location.type) {
    case LegLocationTypeEnum.hospital:
      return caseLocationIds.donorHospitalId === location.id;
    case LegLocationTypeEnum.txcenter:
      return caseLocationIds.txCenterId === location.id;
    default:
      return false;
  }
};

const checkLocationData = (
  locationData: LegLocationType,
  caseLocationIds: CaseLocationIds,
): { type: LegLocationTypeEnum; id: string; location?: Pick<CustomLocationType, 'latitude' | 'longitude'> } | null => {
  switch (locationData.type) {
    case LegLocationTypeEnum.airport:
      const airport = queryClient.getQueryData(airportsQuery.item(locationData.id).queryKey);

      if (airport) return null;

      return { type: LegLocationTypeEnum.airport, id: locationData.id };
    case LegLocationTypeEnum.homeResidence:
      const homeResidence = queryClient.getQueryData(homeLocationQueries.item(locationData.id).queryKey);

      if (homeResidence) return null;

      return { type: LegLocationTypeEnum.homeResidence, id: locationData.id };
    case LegLocationTypeEnum.custom:
      const locationInfo = locationData as BaseLegLocationType<CustomLegLocationType>;

      if (!locationInfo.data) return null;

      return {
        type: LegLocationTypeEnum.custom,
        id: locationData.id,
        location: { latitude: locationInfo.data.location.latitude, longitude: locationInfo.data.location.longitude },
      };
    case LegLocationTypeEnum.hospital:
    case LegLocationTypeEnum.txcenter:
      if (isCaseLocation(locationData, caseLocationIds)) return null;

      return { type: locationData.type, id: locationData.id };
    default:
      return null;
  }
};

type RouteLocationDataType =
  | {
      type: LegLocationTypeEnum.hub;
      data: Hub;
    }
  | {
      type: LegLocationTypeEnum.airport;
      data: AirportType;
    }
  | {
      type: LegLocationTypeEnum.homeResidence;
      data: EmployeeLocation;
    }
  | {
      type: LegLocationTypeEnum.txcenter;
      data: TXCenter;
    }
  | {
      type: LegLocationTypeEnum.hospital;
      data: Hospital;
    }
  | {
      type: LegLocationTypeEnum.custom;
      data: HereGeocodeItemType;
    };

export const fetchLocationsList = async (
  legs: { startLocation: LegLocationType; endLocation: LegLocationType }[],
  caseLocationIds: CaseLocationIds = {},
): Promise<RouteLocationDataType[]> => {
  const missingLocations = legs.reduce<Partial<Record<LegLocationTypeEnum, (string | number)[]>>>((acc, item) => {
    const startLocation = checkLocationData(item.startLocation, caseLocationIds);
    const endLocation = checkLocationData(item.endLocation, caseLocationIds);

    [startLocation, endLocation].forEach((location) => {
      if (location) {
        switch (location.type) {
          case LegLocationTypeEnum.custom:
            if (location.location) {
              acc[location.type] = [location.location.latitude, location.location.longitude];
            }
            break;
          default:
            if (acc[location.type]) {
              acc[location.type] = Array.from(new Set([...acc[location.type]!, location.id]));
            } else {
              acc[location.type] = [location.id];
            }
        }
      }
    });

    return acc;
  }, {});

  const locationTypes = Object.keys(missingLocations) as LegLocationTypeEnum[];

  if (locationTypes) {
    await Promise.all(
      locationTypes.map(async (locationType) => {
        const ids = missingLocations[locationType];

        if (ids?.length) {
          switch (locationType) {
            case LegLocationTypeEnum.airport:
              await queryClient.ensureQueryData(airportsQuery.items(ids as string[], 'ids'));
              break;
            case LegLocationTypeEnum.homeResidence:
              await queryClient.ensureQueryData(homeLocationQueries.list({ ids: ids.join(',') }));
              break;
            case LegLocationTypeEnum.custom:
              await queryClient.ensureQueryData(hereMapsQueries.getAddressByReverseGeocode(ids as number[]));
              break;
            case LegLocationTypeEnum.txcenter:
              await Promise.all((ids as string[]).map((id) => queryClient.ensureQueryData(txCenterQueries.item(id))));
              break;
            case LegLocationTypeEnum.hospital:
              await Promise.all((ids as string[]).map((id) => queryClient.ensureQueryData(hospitalQueries.item(id))));
              break;
            default:
              break;
          }
        }
      }),
    );
  }

  const locationsList = uniqBy(
    legs.flatMap((leg) => [leg.startLocation, leg.endLocation]),
    'id',
  );

  return locationsList
    .map((l) => {
      switch (l.type) {
        case LegLocationTypeEnum.airport:
          return {
            type: l.type,
            data: queryClient.getQueryData(airportsQuery.item(l.id).queryKey),
          };
        case LegLocationTypeEnum.homeResidence:
          return {
            type: l.type,
            data: queryClient.getQueryData(homeLocationQueries.item(l.id).queryKey),
          };
        case LegLocationTypeEnum.hub:
          return {
            type: l.type,
            data: queryClient.getQueryData(hubQueries.item(l.id).queryKey),
          };
        case LegLocationTypeEnum.txcenter:
          if (isCaseLocation(l, caseLocationIds)) return null;

          return {
            type: l.type,
            data: queryClient.getQueryData(txCenterQueries.item(l.id).queryKey),
          };
        case LegLocationTypeEnum.hospital:
          if (isCaseLocation(l, caseLocationIds)) return null;

          return {
            type: l.type,
            data: queryClient.getQueryData(hospitalQueries.item(l.id).queryKey),
          };
        case LegLocationTypeEnum.custom:
          const locationData = l as BaseLegLocationType<CustomLegLocationType>;

          if (!locationData.data) return null;

          return {
            type: locationData.type,
            data: queryClient.getQueryData(
              hereMapsQueries.getAddressByReverseGeocode([
                locationData.data.location.latitude,
                locationData.data.location.longitude,
              ]).queryKey,
            ),
          };
        default:
          return null;
      }
    })
    .filter((d): d is RouteLocationDataType => Boolean(d?.data));
};

export const invalidateAircraftUtilization = () =>
  queryClient.invalidateQueries({ queryKey: routePlannerQueryKeys.getAircraftUtilization() });

export const refetchRouteLogistics = async (logisticsId: string) => {
  const hasLogisticsLogs = queryClient.getQueryData(routePlannerQueries.getLogisticsLogs(logisticsId).queryKey);

  await Promise.all([
    await queryClient.invalidateQueries(routePlannerQueries.getLogisticsById(logisticsId)),
    ...(hasLogisticsLogs
      ? [await queryClient.invalidateQueries(routePlannerQueries.getLogisticsLogs(logisticsId))]
      : []),
  ]);

  // Not awaited: utilization is secondary data and shouldn't delay mutation/ws handlers
  invalidateAircraftUtilization().catch(() => {});
};

const convertResponseLegToRouteLeg = (
  leg: RouteLegResponseType,
  locations: RouteLocationType[],
): RouteLegType | null => {
  if (leg.$type === ResponseRouteLegTypeEnum.AccommodationLeg) {
    return {
      ...omit(leg, '$type'),
      legType: RouteLegTypeEnum.AccommodationLeg,
      checkInAt: new Date(leg.checkInAt).toISOString(),
      checkOutAt: new Date(leg.checkOutAt).toISOString(),
    };
  }

  const departureLocation = locations.find((loc) => loc.id === leg.departureLocation.locationId);
  const arrivalLocation = locations.find((loc) => loc.id === leg.arrivalLocation.locationId);

  const dateTimeData = {
    departureTime: new Date(leg.departureTime).toISOString(),
    departureLocalTimezone: leg.departureLocalTimezone || departureLocation?.location.timezone || '',
    arrivalTime: new Date(leg.arrivalTime).toISOString(),
    arrivalLocalTimezone: leg.arrivalLocalTimezone || arrivalLocation?.location.timezone || '',
  };

  switch (leg.$type) {
    case ResponseRouteLegTypeEnum.TmdxJetAviaLeg:
      return {
        ...omit(leg, '$type'),
        legType: RouteLegTypeEnum.TmdxJetAviaLeg,
        ...dateTimeData,
      };
    case ResponseRouteLegTypeEnum.SupplementalLiftAviaLeg:
      return {
        ...omit(leg, '$type'),
        legType: RouteLegTypeEnum.SupplementalLiftAviaLeg,
        ...dateTimeData,
      };
    case ResponseRouteLegTypeEnum.TxCenterProviderAviaLeg:
      return {
        ...omit(leg, '$type'),
        legType: RouteLegTypeEnum.TxCenterProviderAviaLeg,
        ...dateTimeData,
      };
    case ResponseRouteLegTypeEnum.CommercialFlightAviaLeg:
      return {
        ...omit(leg, '$type'),
        legType: RouteLegTypeEnum.CommercialFlightAviaLeg,
        ...dateTimeData,
      };
    case ResponseRouteLegTypeEnum.TmdxAffiliateGroundLeg:
      return {
        ...omit(leg, '$type'),
        legType: RouteLegTypeEnum.TmdxAffiliateGroundLeg,
        ...dateTimeData,
      };
    case ResponseRouteLegTypeEnum.TmdxDriveAffiliateGroundLeg:
      return {
        ...omit(leg, '$type'),
        legType: RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg,
        ...dateTimeData,
      };
    case ResponseRouteLegTypeEnum.TxCenterProviderGroundLeg:
      return {
        ...omit(leg, '$type'),
        legType: RouteLegTypeEnum.TxCenterProviderGroundLeg,
        ...dateTimeData,
      };
    case ResponseRouteLegTypeEnum.CurriServiceGroundLeg:
      return {
        ...omit(leg, '$type'),
        legType: RouteLegTypeEnum.CurriServiceGroundLeg,
        ...dateTimeData,
      };
    case ResponseRouteLegTypeEnum.TmdxDriveTedsGroundLeg:
      return {
        ...omit(leg, '$type'),
        legType: RouteLegTypeEnum.TmdxDriveTedsGroundLeg,
        ...dateTimeData,
      };
    case ResponseRouteLegTypeEnum.SelfServicedGroundLeg:
      return {
        ...omit(leg, '$type'),
        legType: RouteLegTypeEnum.SelfServicedGroundLeg,
        ...dateTimeData,
      };
    default:
      break;
  }

  return null;
};

const sortByDepartureTime = <T extends RouteLegType>(legs: T[]): T[] => {
  return legs.toSorted((a, b) => {
    const leftStartTime = a.legType === RouteLegTypeEnum.AccommodationLeg ? a.checkInAt : a.departureTime;
    const rightStartTime = b.legType === RouteLegTypeEnum.AccommodationLeg ? b.checkInAt : b.departureTime;

    return differenceInMilliseconds(new Date(leftStartTime), new Date(rightStartTime)) > 0 ? 1 : -1;
  });
};

type RouteLegsDividedType = {
  legs: (AviaLegTypes | GroundLegTypes)[];
  accommodationLegs: AccommodationLeg[];
};

export const getRouteLegsDivided = (legs: RouteLegResponseType[], locations: RouteLocationType[]) => {
  const data = legs.reduce<RouteLegsDividedType>(
    (acc, leg) => {
      const convertedLeg = convertResponseLegToRouteLeg(leg, locations);

      if (!convertedLeg) return acc;

      if (convertedLeg.legType === RouteLegTypeEnum.AccommodationLeg) {
        return {
          ...acc,
          accommodationLegs: [...acc.accommodationLegs, convertedLeg],
        };
      }
      return {
        ...acc,
        legs: [...acc.legs, convertedLeg],
      };
    },
    { legs: [], accommodationLegs: [] },
  );

  return {
    legs: sortByDepartureTime(data.legs),
    accommodationLegs: sortByDepartureTime(data.accommodationLegs),
  };
};

export const handleLogisticsError = (error: AxiosError, variables: Pick<LegIdentifiers, 'logisticsId'>) => {
  if (error.response?.status === 409) {
    refetchRouteLogistics(variables.logisticsId).catch(() => {});
  }

  return error;
};

export const removeLogisticsRoutes = async ({ logisticsId, routes }: RemoveLogisticsRoutesParams) => {
  for await (const route of routes) {
    await api.route.removeRoute({
      logisticsId,
      ...route,
    });
  }
};

export const addHubsToRoute = async ({ logisticsId, hubs }: HubsRequestParams<HubsDataRequestList>) => {
  for await (const { type, ...params } of hubs) {
    const request = type === HubTypeEnum.DepartureHub ? api.route.setDepartureHub : api.route.setArrivalHub;

    await request({
      logisticsId,
      ...params,
    });
  }
};

export const removeHubsFromRoute = async ({ logisticsId, hubs }: HubsRequestParams) => {
  for await (const { type, ...params } of hubs) {
    const request = type === HubTypeEnum.DepartureHub ? api.route.removeDepartureHub : api.route.removeArrivalHub;

    await request({
      logisticsId,
      ...params,
    });
  }
};
